import {
  Button,
  Card,
  DialogHandler,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  Field,
  IconButton,
  Input,
  Select,
  Textarea,
} from "@etain/ui";
import {
  BarChart3,
  Bell,
  BookOpen,
  Building2,
  ChevronDown,
  CircleHelp,
  CircleDollarSign,
  ClipboardList,
  Clock3,
  ContactRound,
  FileText,
  FolderKanban,
  GraduationCap,
  Info,
  LayoutDashboard,
  Lightbulb,
  LockKeyhole,
  LogOut,
  Mail,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Phone,
  ReceiptText,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  UserRound,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { GlobalSearchResponse, NavigationItemId } from "@etain/domain";
import { Fragment, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api.js";
import { appVersion } from "../lib/version.js";
import {
  parseSearchHistory,
  rememberSearchEntry,
  searchHistoryStorageKey,
  toggleFavoriteSearchEntry,
  type SearchHistoryEntry,
} from "../lib/search-history.js";
import {
  globalSearchAccessKey,
  globalSearchQuery,
  globalSearchResult,
  globalSearchStatus,
  isRemoteSearchQuery,
  isSavedSearchVisible,
} from "../lib/global-search.js";
import { useGlobalSearchDelay } from "../lib/global-search-delay.js";
import { helpSectionForPath } from "../lib/help-navigation.js";
import {
  adminSectionForLocation,
  adminSectionPath,
  isAdministrationPath,
  visibleAdminSections,
  type AdminSectionKey,
} from "../lib/admin-navigation.js";
import { roleLabel } from "../lib/format.js";
import { UserAvatar } from "./UserAvatar.js";
import {
  crmPrimarySectionFromPath,
  crmPrimarySections,
  crmSectionFromPath,
  crmBreadcrumbItems,
  crmSections,
} from "../lib/crm-navigation.js";
import { useLogout, useSession, useSwitchTenant } from "../session.js";
import { useNotifications } from "./Notifications.js";
import { NotificationInbox } from "./NotificationInbox.js";
import { IncomingPhoneCalls } from "./IncomingPhoneCalls.js";
import { FormDialog } from "./FormDialog.js";
import { NavigationOrderDialog } from "./NavigationOrderDialog.js";
import { orderNavigationItems, resolveNavigationOrder } from "../lib/navigation-order.js";
import { useNavigationOrder } from "../lib/use-navigation-order.js";

interface NavItem {
  label: string;
  path: string;
  icon: LucideIcon;
  feature?: string;
  description: string;
  activePrefix?: string;
  children?: NavChildItem[];
}

interface NavChildItem {
  label: string;
  path: string;
  icon: LucideIcon;
  description: string;
}

type SearchResult = {
  label: string;
  description: string;
  kind: string;
  path: string;
  icon: LucideIcon;
};

function iconForSearchKind(kind: string): LucideIcon {
  return (
    {
      Organisation: Building2,
      Kontakt: ContactRound,
      Interessent: UserRound,
      Deal: CircleDollarSign,
      Angebot: ReceiptText,
      Aktivität: ClipboardList,
      Projekt: FolderKanban,
      Aufgabe: ClipboardList,
      Wissen: BookOpen,
      Datei: FileText,
      "CRM-Datei": FileText,
      Zielgruppe: Users,
      Telefonnotiz: Phone,
      "E-Mail": Mail,
      Befragung: ClipboardList,
      Befragungsfrage: ClipboardList,
      Wissensfreigabe: BookOpen,
      Aktion: Plus,
      Bereich: LayoutDashboard,
    }[kind] ?? Search
  );
}

function savedSearchResult(entry: SearchHistoryEntry): SearchResult {
  return { ...entry, icon: iconForSearchKind(entry.kind) };
}

function storageEntry(result: SearchResult): SearchHistoryEntry {
  return {
    label: result.label,
    description: result.description,
    kind: result.kind,
    path: result.path,
  };
}

const crmPrimarySectionIcons: Record<(typeof crmPrimarySections)[number]["key"], LucideIcon> = {
  sales: CircleDollarSign,
  customers: Building2,
  activities: ClipboardList,
  email: Mail,
  more: SlidersHorizontal,
};

const crmSectionIcons: Record<(typeof crmSections)[number]["key"], LucideIcon> = {
  dashboard: LayoutDashboard,
  leads: UserRound,
  organizations: Building2,
  contacts: ContactRound,
  deals: CircleDollarSign,
  offers: ReceiptText,
  communications: Mail,
  documents: FileText,
  activities: ClipboardList,
};

const navItems: (NavItem & { id: NavigationItemId })[] = [
  {
    id: "overview",
    label: "Übersicht",
    path: "/",
    icon: LayoutDashboard,
    description: "Wichtiges und nächste Schritte",
  },
  {
    id: "crm",
    label: "CRM",
    path: "/crm/vertrieb",
    icon: Users,
    feature: "crm",
    description: "Vertrieb und Kunden einfach steuern",
    activePrefix: "/crm/",
    children: crmPrimarySections.map((section) => ({
      label: section.label,
      path: section.path,
      icon: crmPrimarySectionIcons[section.key],
      description: section.description,
    })),
  },
  {
    id: "projects",
    label: "Projekte",
    path: "/projekte",
    icon: FolderKanban,
    feature: "projects",
    description: "Projekte und Aufgaben",
  },
  {
    id: "finance",
    label: "Finanzen",
    path: "/finanzen",
    icon: ReceiptText,
    feature: "finance",
    description: "Belege und Mahnungen",
  },
  {
    id: "files",
    label: "Dateiexplorer",
    path: "/dateiexplorer",
    icon: FolderKanban,
    description: "Dateien und gemeinsame Ablagen",
  },
  {
    id: "marketing",
    label: "Marketing",
    path: "/marketing",
    icon: Mail,
    feature: "marketing",
    description: "Kampagnen und Einwilligungen",
  },
  {
    id: "learning",
    label: "Lernen",
    path: "/lernen",
    icon: GraduationCap,
    feature: "learning",
    description: "Kurse und Fortschritt",
  },
  {
    id: "bem",
    label: "BEM",
    path: "/bem",
    icon: ShieldCheck,
    feature: "bem",
    description: "Geschützte BEM-Fallbearbeitung",
    activePrefix: "/bem",
  },
  {
    id: "surveys",
    label: "Befragungen",
    path: "/befragungen",
    icon: ClipboardList,
    feature: "surveys",
    description: "Fragebögen und Ergebnisse",
  },
  {
    id: "knowledge",
    label: "Wissensdatenbank",
    path: "/wissen",
    icon: BookOpen,
    feature: "knowledge",
    description: "Wissen finden und teilen",
    activePrefix: "/wissen",
  },
  {
    id: "reports",
    label: "Berichte",
    path: "/berichte",
    icon: BarChart3,
    feature: "reports",
    description: "Kennzahlen im Zusammenhang",
  },
];

const defaultNavigationOrder = navItems.map((item) => item.id);

const adminSectionIcons: Record<AdminSectionKey, LucideIcon> = {
  features: SlidersHorizontal,
  knowledge: BookOpen,
  users: Users,
  products: ReceiptText,
  projects: FolderKanban,
  crm: ContactRound,
  bem: ShieldCheck,
  marketing: Mail,
  surveys: BarChart3,
  email: Mail,
  integrations: Settings,
  "file-explorer": FileText,
  ideas: Lightbulb,
  audit: ClipboardList,
  tenants: Building2,
  "technical-administration": BookOpen,
  security: LockKeyhole,
};

function AdministrationNavigation({
  items,
  activePath,
  compact,
  onNavigate,
}: {
  items: NavItem[];
  activePath?: string;
  compact?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <nav className="main-nav main-nav--administration" aria-label="Administrationsnavigation">
      {items.map((item) => {
        const Icon = item.icon;
        const active = item.path === activePath;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onNavigate}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
            className={`main-nav__item${active ? " main-nav__item--active" : ""}`}
            title={compact ? item.label : undefined}
          >
            <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
            <span className="main-nav__label">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

const quickActions = [
  {
    label: "Interessent anlegen",
    path: "/crm/vertrieb?neu=interessent",
    icon: UserRound,
    feature: "crmWrite",
  },
  {
    label: "Kunde anlegen",
    path: "/crm/kunden?neu=organisation",
    icon: Building2,
    feature: "crmWrite",
  },
  {
    label: "Kontakt anlegen",
    path: "/crm/kontakte?neu=kontakt",
    icon: Users,
    feature: "crmWrite",
  },
  {
    label: "Deal anlegen",
    path: "/crm/deals?neu=deal",
    icon: CircleDollarSign,
    feature: "crmWrite",
  },
  {
    label: "Aktivität anlegen",
    path: `/crm/aktivitaeten?neu=${encodeURIComponent("aktivität")}`,
    icon: ClipboardList,
    feature: "crmWrite",
  },
  {
    label: "Projekt anlegen",
    path: "/projekte?neu=projekt",
    icon: FolderKanban,
    feature: "projectsWrite",
  },
  {
    label: "Kampagne anlegen",
    path: "/marketing?neu=kampagne",
    icon: Mail,
    feature: "marketingWrite",
  },
  { label: "Kurs anlegen", path: "/lernen?neu=kurs", icon: BookOpen, feature: "learningWrite" },
  {
    label: "Befragung verbinden",
    path: "/befragungen?neu=befragung",
    icon: ClipboardList,
    feature: "surveysWrite",
  },
];

// Ab dieser Breite reicht der Platz in der Sidebar nicht mehr für ausgeschriebene Labels –
// unterhalb davon klappt die Sidebar automatisch ein, unabhängig vom manuellen Schalter.
const AUTO_COMPACT_QUERY = "(max-width: 1240px)";

function getAutoCompactSnapshot(): boolean {
  return typeof window !== "undefined" && window.matchMedia(AUTO_COMPACT_QUERY).matches;
}

function getAutoCompactServerSnapshot(): boolean {
  return false;
}

function subscribeAutoCompact(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const mediaQueryList = window.matchMedia(AUTO_COMPACT_QUERY);
  mediaQueryList.addEventListener("change", onStoreChange);
  return () => mediaQueryList.removeEventListener("change", onStoreChange);
}

function useAutoCompactSidebar(): boolean {
  return useSyncExternalStore(
    subscribeAutoCompact,
    getAutoCompactSnapshot,
    getAutoCompactServerSnapshot,
  );
}

function HelpLink({ compact, onNavigate }: { compact?: boolean; onNavigate?: () => void }) {
  return (
    <NavLink
      to="/hilfe"
      onClick={onNavigate}
      className={({ isActive }) =>
        `documentation-link${isActive ? " documentation-link--active" : ""}`
      }
      aria-label="Hilfe"
      title={compact ? "Hilfe" : undefined}
    >
      <CircleHelp size={17} aria-hidden="true" />
      <span className="documentation-link__label">Hilfe</span>
    </NavLink>
  );
}

function Navigation({
  items,
  compact,
  onNavigate,
}: {
  items: NavItem[];
  compact?: boolean;
  onNavigate?: () => void;
}) {
  const location = useLocation();
  return (
    <nav className="main-nav" aria-label="Hauptnavigation">
      {items.map((item) => {
        const Icon = item.icon;
        const groupActive = item.activePrefix
          ? location.pathname.startsWith(item.activePrefix)
          : false;
        return (
          <div className="main-nav__group" key={item.path}>
            <NavLink
              to={item.path}
              end={item.path === "/"}
              onClick={onNavigate}
              aria-label={item.label}
              className={({ isActive }) =>
                `main-nav__item${
                  item.children
                    ? isActive || groupActive
                      ? " main-nav__item--group-active"
                      : ""
                    : isActive
                      ? " main-nav__item--active"
                      : ""
                }`
              }
              title={compact ? item.label : undefined}
            >
              <Icon size={20} strokeWidth={1.9} aria-hidden="true" />
              <span className="main-nav__label">{item.label}</span>
            </NavLink>
            {item.children ? (
              <div
                className="main-nav__submenu"
                role="group"
                aria-label={`${item.label}-Untermenü`}
              >
                {item.children.map((child) => {
                  const ChildIcon = child.icon;
                  const childActive =
                    location.pathname === child.path ||
                    (child.path === "/crm/mehr" &&
                      location.pathname.startsWith("/crm/") &&
                      !["/crm/vertrieb", "/crm/kunden", "/crm/aktivitaeten", "/crm/e-mail"].some(
                        (path) =>
                          location.pathname === path || location.pathname.startsWith(path + "/"),
                      )) ||
                    ((child.path === "/crm/organisationen" || child.path === "/crm/kunden") &&
                      location.pathname.startsWith(`${child.path}/`));
                  return (
                    <NavLink
                      key={child.path}
                      to={child.path}
                      end={child.path !== "/crm/organisationen" && child.path !== "/crm/kunden"}
                      onClick={onNavigate}
                      aria-label={child.label}
                      className={({ isActive }) =>
                        `main-nav__subitem${isActive || childActive ? " main-nav__subitem--active" : ""}`
                      }
                      title={compact ? child.label : undefined}
                    >
                      <ChildIcon size={15} strokeWidth={1.9} aria-hidden="true" />
                      <span className="main-nav__label">{child.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}

// Sprechende Modulnamen für die Weiterleitungsbegründung in BlockedFeatureNotice. In
// packages/domain/src/module-features.ts gibt es nur die technischen Feature-Schlüssel
// (TenantModule), keine Anzeige-Labels – daher hier die gleichen deutschen Bezeichnungen
// wie in der Navigation (oben in dieser Datei) verwendet, statt neue Texte zu erfinden.
const featureLabels: Record<string, string> = {
  crm: "CRM",
  projects: "Projekte",
  finance: "Finanzen",
  marketing: "Marketing",
  learning: "Lernen",
  surveys: "Befragungen",
  reports: "Berichte",
  admin: "Administration",
  documentation: "Technische Administration",
  knowledge: "Wissensdatenbank",
};

interface BlockedFeatureState {
  blockedFeature?: string;
}

function BlockedFeatureNotice({ feature }: { feature: string }) {
  const label = featureLabels[feature] ?? feature;
  return (
    <div className="blocked-feature-notice" role="status">
      <Card className="blocked-feature-notice__card">
        <Info aria-hidden="true" />
        <p>
          Das Modul „{label}" ist für Ihr Konto nicht freigeschaltet. Sie wurden deshalb zur
          Übersicht weitergeleitet.
        </p>
      </Card>
    </div>
  );
}

export function AppShell() {
  const { data: session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const switchTenant = useSwitchTenant();
  const { notify } = useNotifications();
  const [sidebarCompact, setSidebarCompact] = useState(false);
  const autoCompact = useAutoCompactSidebar();
  const compact = sidebarCompact || autoCompact;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeResultIndex, setActiveResultIndex] = useState(-1);
  const [searchStorageRevision, setSearchStorageRevision] = useState(0);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [ideaDialogOpen, setIdeaDialogOpen] = useState(false);
  const [ideaDescription, setIdeaDescription] = useState("");
  const searchInput = useRef<HTMLInputElement>(null);
  const userMenuTrigger = useRef<HTMLButtonElement>(null);
  const searchContainer = useRef<HTMLDivElement>(null);
  const sessionUserId = session?.user.id ?? "";
  const activeTenantId = session?.activeTenant.id ?? "";
  const navigationScope = `${sessionUserId}:${activeTenantId}`;
  const {
    order: savedNavigationOrder,
    query: navigationPreferences,
    save: saveNavigationOrder,
  } = useNavigationOrder(sessionUserId, activeTenantId);
  const navigationOrder = resolveNavigationOrder(defaultNavigationOrder, savedNavigationOrder);
  const [navigationDialogScope, setNavigationDialogScope] = useState<string | null>(null);
  const administrationMode =
    session?.features.admin === true && isAdministrationPath(location.pathname);
  const administrationSection = adminSectionForLocation(
    location.pathname,
    location.search,
    session?.features ?? {},
    session?.activeTenant.kind,
  );
  const administrationNav = useMemo(
    () =>
      visibleAdminSections(session?.features ?? {}, session?.activeTenant.kind).map((section) => ({
        ...section,
        path: adminSectionPath(section.key),
        icon: adminSectionIcons[section.key],
      })),
    [session?.features, session?.activeTenant],
  );
  const workLocation = useRef<{ userId: string; tenantId: string; path: string } | null>(null);
  useEffect(() => {
    if (!isAdministrationPath(location.pathname)) {
      workLocation.current = {
        userId: sessionUserId,
        tenantId: activeTenantId,
        path: `${location.pathname}${location.search}${location.hash}`,
      };
    }
  }, [activeTenantId, sessionUserId, location.pathname, location.search, location.hash]);
  const submitIdea = useMutation({
    mutationFn: (body: { menuContext: string; description: string }) => api.post("/ideas", body),
    onSuccess: () => {
      setIdeaDialogOpen(false);
      setIdeaDescription("");
      notify("Vielen Dank – Ihre Idee wurde eingereicht.");
    },
    onError: (error) =>
      notify(
        error instanceof Error ? error.message : "Die Idee konnte nicht eingereicht werden.",
        "error",
      ),
  });

  const visibleNav = useMemo(() => {
    const features = session?.features ?? {};
    return orderNavigationItems(navItems, savedNavigationOrder)
      .filter((item) => !item.feature || features[item.feature] === true)
      .map((item) =>
        item.label === "CRM" && features.crmSalesWorkspace !== true
          ? {
              ...item,
              path: "/crm/dashboard",
              description: "Kundenbeziehungen und Fachlisten",
              children: [
                ...crmSections.map((section) => ({
                  label: section.label,
                  path: section.path,
                  icon: crmSectionIcons[section.key],
                  description: section.description,
                })),
                {
                  label: "E-Mail-Eingang",
                  path: "/crm/e-mail",
                  icon: Mail,
                  description: "Persönliche Kundenmails zuordnen und bearbeiten",
                },
              ],
            }
          : item,
      );
  }, [session?.features, savedNavigationOrder]);
  const visibleQuickActions = useMemo(() => {
    const features = session?.features ?? {};
    return quickActions
      .filter((item) => features[item.feature] === true)
      .map((item) =>
        item.label === "Interessent anlegen" && features.crmSalesWorkspace !== true
          ? { ...item, label: "Interessent anlegen", path: "/crm/leads?neu=lead" }
          : item,
      );
  }, [session?.features]);
  const query = search.trim();
  const actionQuery = query.startsWith("/");
  const salesWorkspace = session?.features.crmSalesWorkspace === true;
  const canSearchRecords =
    !!session &&
    (session.user.role !== "learner" ||
      ["crm", "projects", "knowledge", "surveys"].some(
        (module) => session.features[module] === true,
      ));

  const favorites = useMemo(
    () =>
      searchStorageRevision >= 0 && sessionUserId && activeTenantId
        ? parseSearchHistory(
            window.localStorage.getItem(
              searchHistoryStorageKey(sessionUserId, activeTenantId, "favorites"),
            ),
          )
        : [],
    [activeTenantId, searchStorageRevision, sessionUserId],
  );
  const recent = useMemo(
    () =>
      searchStorageRevision >= 0 && sessionUserId && activeTenantId
        ? parseSearchHistory(
            window.localStorage.getItem(
              searchHistoryStorageKey(sessionUserId, activeTenantId, "recent"),
            ),
          )
        : [],
    [activeTenantId, searchStorageRevision, sessionUserId],
  );
  const accessKey = globalSearchAccessKey({
    userId: sessionUserId,
    tenantId: activeTenantId,
    role: session?.user.role ?? "",
    features: session?.features ?? {},
  });
  const requestedSearch =
    sessionUserId &&
    activeTenantId &&
    searchFocused &&
    canSearchRecords &&
    !switchTenant.isPending &&
    isRemoteSearchQuery(query)
      ? JSON.stringify([accessKey, query])
      : null;
  const delayedSearch = useGlobalSearchDelay(requestedSearch);
  const searchReady = requestedSearch !== null && delayedSearch === requestedSearch;
  const recordSearch = useQuery({
    queryKey: ["global-search", accessKey, requestedSearch],
    queryFn: async ({ signal }) => ({
      requestKey: requestedSearch,
      response: await api.get<GlobalSearchResponse>(`/search?${globalSearchQuery(query)}`, signal),
    }),
    enabled: searchReady,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  const recordData =
    searchReady &&
    !recordSearch.isError &&
    !recordSearch.isFetching &&
    recordSearch.data?.requestKey === requestedSearch &&
    recordSearch.data.response.query === query
      ? recordSearch.data.response
      : undefined;
  const results = useMemo<SearchResult[]>(() => {
    const needle = (actionQuery ? query.slice(1) : query).trim().toLocaleLowerCase("de-DE");
    const paletteItems: SearchResult[] = actionQuery
      ? visibleQuickActions.map((item) => ({
          ...item,
          description: "Direkt erstellen",
          kind: "Aktion",
        }))
      : [
          ...visibleNav.flatMap((item) => [
            { ...item, kind: "Bereich" },
            ...(item.children ?? []).map((child) => ({ ...child, kind: item.label })),
          ]),
          ...visibleQuickActions.map((item) => ({
            ...item,
            description: "Direkt erstellen – mit / ausführen",
            kind: "Aktion",
          })),
        ];
    const paletteResults = paletteItems.filter((item) =>
      `${item.label} ${item.description}`.toLocaleLowerCase("de-DE").includes(needle),
    );
    if (actionQuery || !recordData) return paletteResults;
    const records = recordData.items.map((item) => {
      const result = globalSearchResult(item, salesWorkspace);
      return { ...result, icon: iconForSearchKind(result.kind) };
    });
    return [...records, ...paletteResults];
  }, [actionQuery, query, recordData, salesWorkspace, visibleNav, visibleQuickActions]);

  const favoriteResults = useMemo(
    () =>
      favorites
        .filter((entry) => isSavedSearchVisible(entry.path, session?.features ?? {}))
        .map(savedSearchResult),
    [favorites, session?.features],
  );
  const recentResults = useMemo(() => {
    const favoritePaths = new Set(favorites.map((item) => item.path));
    return recent
      .filter(
        (item) =>
          !favoritePaths.has(item.path) && isSavedSearchVisible(item.path, session?.features ?? {}),
      )
      .map(savedSearchResult);
  }, [favorites, recent, session?.features]);
  const shownResults = useMemo(
    () => (query ? results : [...favoriteResults, ...recentResults].slice(0, 8)),
    [favoriteResults, query, recentResults, results],
  );
  const favoritePaths = useMemo(() => new Set(favorites.map((item) => item.path)), [favorites]);

  const searchStatus = globalSearchStatus({
    query,
    debouncing: canSearchRecords && isRemoteSearchQuery(query) && !searchReady,
    fetching: recordSearch.isFetching || (searchReady && recordSearch.isPending),
    error: searchReady && recordSearch.isError,
    failedSources: recordData?.failedSources ?? [],
    resultCount: shownResults.length,
  });

  const effectiveActiveResultIndex =
    activeResultIndex >= 0 && activeResultIndex < shownResults.length ? activeResultIndex : -1;

  useEffect(() => {
    if (searchFocused && effectiveActiveResultIndex >= 0)
      document
        .getElementById(`global-search-result-${effectiveActiveResultIndex}`)
        ?.scrollIntoView({ block: "nearest" });
  }, [effectiveActiveResultIndex, searchFocused]);

  useEffect(() => {
    function focusActionSearch(event: KeyboardEvent) {
      const target = event.target as HTMLElement | null;
      if (
        event.key !== "/" ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey ||
        target?.matches("input, textarea, select, [contenteditable]") ||
        document.querySelector('[role="dialog"][data-state="open"]')
      )
        return;
      event.preventDefault();
      setSearch("/");
      setSearchFocused(true);
      searchInput.current?.focus();
    }
    window.addEventListener("keydown", focusActionSearch);
    return () => window.removeEventListener("keydown", focusActionSearch);
  }, []);

  useEffect(() => {
    function closeSearchOnOutsideClick(event: PointerEvent) {
      if (searchContainer.current?.contains(event.target as Node)) return;
      setSearchFocused(false);
      setActiveResultIndex(-1);
    }

    document.addEventListener("pointerdown", closeSearchOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeSearchOnOutsideClick);
  }, []);

  if (!session) return null;

  const blockedFeature = (location.state as BlockedFeatureState | null)?.blockedFeature;
  const currentCrmSection =
    crmPrimarySectionFromPath(location.pathname) ?? crmSectionFromPath(location.pathname);
  const currentNav = navItems.find((item) => {
    if (item.path === "/") return location.pathname === "/";
    if (item.activePrefix) return location.pathname.startsWith(item.activePrefix);
    return location.pathname.startsWith(item.path);
  });
  const currentLabel = administrationMode
    ? "Administration"
    : location.pathname.startsWith("/einstellungen/profil")
      ? "Mein Profil"
      : location.pathname.startsWith("/dokumentation")
        ? "Technische Administration"
        : location.pathname.startsWith("/hilfe")
          ? "Hilfe"
          : (currentNav?.label ?? "Bereich");
  const mayAccessAdministration = session.features.admin === true;
  const ideaContext = [currentLabel, administrationSection?.label ?? currentCrmSection?.label]
    .filter(Boolean)
    .join(" / ");
  const currentHelpSection = helpSectionForPath(location.pathname);
  const openCurrentHelp = () => navigate(`/hilfe?bereich=${currentHelpSection}`);
  // Zwischeneinträge zeigen auf den jeweiligen Bereich, der letzte Eintrag bleibt ohne Link.
  const breadcrumbItems: { label: string; path?: string }[] = [
    { label: "ETAIN Hub", path: "/" },
    ...(crmBreadcrumbItems(location.pathname) ?? [
      { label: currentLabel },
      ...(administrationMode && administrationSection
        ? [{ label: administrationSection.label }]
        : []),
    ]),
  ];
  const moduleBreadcrumb =
    session.features.surveys === true && location.pathname.startsWith("/befragungen/");

  function toggleAdministration() {
    const previousWork = workLocation.current;
    const returnPath =
      previousWork?.userId === sessionUserId && previousWork.tenantId === activeTenantId
        ? previousWork.path
        : "/";
    navigate(administrationMode ? returnPath : "/administration");
  }

  async function handleTenantChange(tenantId: string) {
    try {
      await switchTenant.mutateAsync(tenantId);
      notify("Mandant wurde gewechselt.");
      navigate("/");
    } catch (error) {
      notify(
        error instanceof Error ? error.message : "Mandant konnte nicht gewechselt werden.",
        "error",
      );
    }
  }

  function persistSearchEntries(category: "favorites" | "recent", entries: SearchHistoryEntry[]) {
    window.localStorage.setItem(
      searchHistoryStorageKey(session!.user.id, session!.activeTenant.id, category),
      JSON.stringify(entries),
    );
  }

  function toggleFavorite(result: SearchResult) {
    const next = toggleFavoriteSearchEntry(favorites, storageEntry(result));
    persistSearchEntries("favorites", next);
    setSearchStorageRevision((current) => current + 1);
  }

  function openResult(result: SearchResult) {
    const next = rememberSearchEntry(recent, storageEntry(result));
    persistSearchEntries("recent", next);
    setSearchStorageRevision((current) => current + 1);
    setSearch("");
    setSearchFocused(false);
    setActiveResultIndex(-1);
    navigate(result.path);
  }

  return (
    <div className={`app-layout${compact ? " app-layout--compact" : ""}`}>
      <a className="skip-link" href="#main-content">
        Zum Hauptinhalt springen
      </a>
      <aside className="sidebar">
        <div className="sidebar__brand">
          <img src="/brand/etain-logo.svg" alt="ETAIN" />
          <span>Hub</span>
        </div>
        {administrationMode ? (
          <>
            <div className="sidebar__mode" title="Administrationsmodus">
              <ShieldCheck size={18} aria-hidden="true" />
              <span className="main-nav__label">Administrationsmodus</span>
            </div>
            <AdministrationNavigation
              items={administrationNav}
              activePath={administrationSection && adminSectionPath(administrationSection.key)}
              compact={compact}
            />
          </>
        ) : (
          <Navigation items={visibleNav} compact={compact} />
        )}
        <div className="sidebar__footer">
          <div className="sidebar__meta">
            {autoCompact ? null : (
              <IconButton
                aria-label={sidebarCompact ? "Navigation ausklappen" : "Navigation einklappen"}
                onClick={() => setSidebarCompact((current) => !current)}
              >
                {sidebarCompact ? (
                  <PanelLeftOpen aria-hidden="true" />
                ) : (
                  <PanelLeftClose aria-hidden="true" />
                )}
              </IconButton>
            )}
            <span className="sidebar__version">Version {appVersion}</span>
          </div>
          <HelpLink compact={compact} />
        </div>
      </aside>

      <header className="topbar">
        <IconButton
          className="topbar__mobile-menu"
          aria-label="Navigation öffnen"
          onClick={() => setMobileOpen(true)}
        >
          <Menu aria-hidden="true" />
        </IconButton>

        <div
          ref={searchContainer}
          className="global-search"
          onMouseDownCapture={(event) => {
            // Safari otherwise blurs the input before activating palette buttons.
            if (event.target instanceof Element && event.target.closest("button"))
              event.preventDefault();
          }}
          onBlurCapture={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
              setSearchFocused(false);
              setActiveResultIndex(-1);
            }
          }}
        >
          <Search size={19} aria-hidden="true" />
          <label className="ui-sr-only" htmlFor="global-search">
            Alles suchen oder Aktion ausführen
          </label>
          <input
            ref={searchInput}
            id="global-search"
            type="search"
            maxLength={200}
            autoComplete="off"
            placeholder="Alles suchen oder / Aktion …"
            value={search}
            role="combobox"
            aria-autocomplete="list"
            aria-controls="global-search-results"
            aria-expanded={searchFocused && (Boolean(query) || shownResults.length > 0)}
            aria-activedescendant={
              searchFocused && effectiveActiveResultIndex >= 0
                ? `global-search-result-${effectiveActiveResultIndex}`
                : undefined
            }
            onChange={(event) => {
              setSearch(event.target.value);
              setActiveResultIndex(-1);
            }}
            onFocus={() => setSearchFocused(true)}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                setSearch("");
                setSearchFocused(false);
                setActiveResultIndex(-1);
                event.currentTarget.blur();
                return;
              }
              if (event.key === "ArrowDown" && shownResults.length) {
                event.preventDefault();
                setActiveResultIndex((current) =>
                  current >= shownResults.length - 1 ? 0 : current + 1,
                );
                return;
              }
              if (event.key === "ArrowUp" && shownResults.length) {
                event.preventDefault();
                setActiveResultIndex((current) =>
                  current <= 0 ? shownResults.length - 1 : current - 1,
                );
                return;
              }
              if (event.key === "Enter") {
                const result =
                  shownResults[effectiveActiveResultIndex >= 0 ? effectiveActiveResultIndex : 0];
                if (!result) return;
                event.preventDefault();
                openResult(result);
              }
            }}
          />
          {search ? (
            <button type="button" onClick={() => setSearch("")} aria-label="Suche leeren">
              <X size={16} aria-hidden="true" />
            </button>
          ) : (
            <kbd>/</kbd>
          )}
          {searchFocused && (Boolean(query) || shownResults.length > 0) ? (
            <div className="global-search__results">
              {/* Die Listbox besitzt nur Optionen. aria-owns verbindet die Optionen
                  mit ihr, während Favoriten und Status außerhalb der Listbox bleiben. */}
              {searchStatus ? (
                <div role="status" aria-live="polite">
                  <p>{searchStatus.message}</p>
                  {searchStatus.canRetry ? (
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => {
                        searchInput.current?.focus();
                        void recordSearch.refetch();
                      }}
                    >
                      Suche wiederholen
                    </Button>
                  ) : null}
                </div>
              ) : !query ? (
                <p className="global-search__saved-heading">
                  <Clock3 size={14} aria-hidden="true" /> Favoriten und zuletzt geöffnet
                </p>
              ) : null}
              <div
                id="global-search-results"
                role="listbox"
                aria-label="Suchergebnisse"
                aria-owns={
                  shownResults.length
                    ? shownResults.map((_, index) => `global-search-result-${index}`).join(" ")
                    : undefined
                }
              />
              {shownResults.map((result, index) => {
                const Icon = result.icon;
                const favorite = favoritePaths.has(result.path);
                return (
                  <div className="global-search__result" key={`${result.kind}-${result.path}`}>
                    <button
                      id={`global-search-result-${index}`}
                      type="button"
                      role="option"
                      aria-selected={effectiveActiveResultIndex === index}
                      className={effectiveActiveResultIndex === index ? "is-active" : undefined}
                      onMouseEnter={() => setActiveResultIndex(index)}
                      onClick={() => openResult(result)}
                    >
                      <Icon size={18} aria-hidden="true" />
                      <span>
                        <strong>{result.label}</strong>
                        <small>
                          {!query ? `${favorite ? "Favorit" : "Zuletzt geöffnet"} · ` : ""}
                          {result.kind} · {result.description}
                        </small>
                      </span>
                    </button>
                    <button
                      type="button"
                      className="global-search__favorite"
                      aria-label={`${result.label} ${
                        favorite ? "aus Favoriten entfernen" : "zu Favoriten hinzufügen"
                      }`}
                      aria-pressed={favorite}
                      onClick={() => toggleFavorite(result)}
                    >
                      <Star
                        size={16}
                        fill={favorite ? "currentColor" : "none"}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>

        <div className="topbar__actions">
          {visibleQuickActions.length ? (
            <DropdownMenu
              align="end"
              trigger={
                <Button className="quick-action" size="small" aria-label="Neu anlegen">
                  <Plus size={17} aria-hidden="true" />
                  <span>Neu</span>
                  <ChevronDown size={15} aria-hidden="true" />
                </Button>
              }
            >
              {visibleQuickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <DropdownMenuItem key={action.path} onSelect={() => navigate(action.path)}>
                    <Icon size={17} aria-hidden="true" /> {action.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenu>
          ) : null}

          <IconButton
            className="topbar__optional"
            aria-label="Hilfe zum aktuellen Bereich"
            onClick={openCurrentHelp}
          >
            <CircleHelp aria-hidden="true" />
          </IconButton>
          <NotificationInbox
            key={`${session.activeTenant.id}:${session.user.id}:${session.features.crm}:${session.features.projects}`}
            open={remindersOpen}
            onOpenChange={setRemindersOpen}
          />
          <IconButton
            className="topbar__optional"
            aria-label="Idee einreichen"
            title="Idee einreichen"
            onClick={() => setIdeaDialogOpen(true)}
          >
            <Lightbulb aria-hidden="true" />
          </IconButton>

          <div className="tenant-switcher">
            <label className="ui-sr-only" htmlFor="tenant-switcher">
              Aktiver Mandant
            </label>
            <Select
              id="tenant-switcher"
              value={session.activeTenant.id}
              onChange={(event) => handleTenantChange(event.target.value)}
              disabled={switchTenant.isPending || session.tenants.length < 2}
              aria-label="Aktiven Mandanten wechseln"
            >
              {session.tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </div>

          <DropdownMenu
            trigger={
              <button
                ref={userMenuTrigger}
                className="user-trigger"
                type="button"
                aria-label="Benutzermenü öffnen"
              >
                <UserAvatar userId={session.user.id} name={session.user.name} />
                <span className="user-trigger__copy">
                  <strong>{session.user.name}</strong>
                  <small>{roleLabel(session.user.role)}</small>
                </span>
                <ChevronDown size={15} aria-hidden="true" />
              </button>
            }
          >
            <div className="user-menu__summary">
              <strong>{session.user.name}</strong>
              <span>{session.user.email}</span>
              <small>{session.activeTenant.name}</small>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={openCurrentHelp}>
              <CircleHelp size={17} aria-hidden="true" /> Hilfe zum aktuellen Bereich
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setRemindersOpen(true)}>
              <Bell size={17} aria-hidden="true" /> Benachrichtigungen
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setIdeaDialogOpen(true)}>
              <Lightbulb size={17} aria-hidden="true" /> Idee einreichen
            </DropdownMenuItem>
            {mayAccessAdministration ? (
              <DropdownMenuItem onSelect={toggleAdministration}>
                {administrationMode ? (
                  <LogOut size={17} aria-hidden="true" />
                ) : (
                  <Settings size={17} aria-hidden="true" />
                )}
                {administrationMode ? "Zurück zum Arbeitsmodus" : "Administration"}
              </DropdownMenuItem>
            ) : null}
            {session.features.crm ? (
              <DropdownMenuItem onSelect={() => navigate("/einstellungen/e-mail")}>
                <Mail size={17} aria-hidden="true" /> Meine E-Mail-Einstellungen
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem onSelect={() => navigate("/einstellungen/profil")}>
              <Settings size={17} aria-hidden="true" /> Profileinstellungen
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => setNavigationDialogScope(navigationScope)}>
              <SlidersHorizontal size={17} aria-hidden="true" /> Menüreihenfolge anpassen
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={logout.isPending}
              onSelect={() =>
                logout.mutate(undefined, {
                  onSuccess: () => navigate("/anmelden", { replace: true }),
                  onError: (error) =>
                    notify(
                      error instanceof Error
                        ? error.message
                        : "Die sichere Abmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.",
                      "error",
                    ),
                })
              }
            >
              <LogOut size={17} aria-hidden="true" /> Abmelden
            </DropdownMenuItem>
          </DropdownMenu>
        </div>
      </header>

      <main
        id="main-content"
        className="content"
        tabIndex={-1}
        inert={searchFocused && (Boolean(query) || shownResults.length > 0)}
      >
        {!moduleBreadcrumb ? (
          <nav className="breadcrumb" aria-label="Navigationspfad">
            {breadcrumbItems.map((item, index) => {
              const isLast = index === breadcrumbItems.length - 1;
              return (
                <Fragment key={`${item.label}-${index}`}>
                  {index > 0 ? <span aria-hidden="true">/</span> : null}
                  {isLast ? (
                    <strong aria-current="page">{item.label}</strong>
                  ) : item.path ? (
                    <Link to={item.path}>{item.label}</Link>
                  ) : (
                    <span>{item.label}</span>
                  )}
                </Fragment>
              );
            })}
          </nav>
        ) : null}
        {blockedFeature ? <BlockedFeatureNotice feature={blockedFeature} /> : null}
        <Outlet />
      </main>

      <section id="crm-call-stack" className="crm-phone-call-stack" aria-label="Telefonanrufe" />
      <IncomingPhoneCalls />

      <NavigationOrderDialog
        key={navigationScope}
        open={navigationDialogScope === navigationScope}
        onOpenChange={(open) => setNavigationDialogScope(open ? navigationScope : null)}
        items={visibleNav}
        order={navigationOrder}
        defaultOrder={defaultNavigationOrder}
        loading={navigationPreferences.isPending}
        loadError={navigationPreferences.isError}
        busy={saveNavigationOrder.isPending}
        restoreFocusRef={userMenuTrigger}
        onRetry={() => void navigationPreferences.refetch()}
        onSave={async (order) => {
          await saveNavigationOrder.mutateAsync(order);
          setNavigationDialogScope(null);
          notify("Menüreihenfolge gespeichert.");
        }}
      />

      <FormDialog
        open={ideaDialogOpen}
        onOpenChange={(open) => {
          setIdeaDialogOpen(open);
          if (!open) setIdeaDescription("");
        }}
        title="Idee einreichen"
        description="Ihre Rückmeldung hilft uns, den ETAIN Hub gezielt weiterzuentwickeln."
        submitLabel="Absenden"
        busy={submitIdea.isPending}
        submitDisabled={!ideaDescription.trim()}
        size="medium"
        onSubmit={(event) => {
          event.preventDefault();
          submitIdea.mutate({ menuContext: ideaContext, description: ideaDescription.trim() });
        }}
      >
        <Field label="Menü / Untermenü" htmlFor="idea-menu-context">
          <Input id="idea-menu-context" value={ideaContext} readOnly />
        </Field>
        <Field label="Idee beschreiben" htmlFor="idea-description" required>
          <Textarea
            id="idea-description"
            value={ideaDescription}
            onChange={(event) => setIdeaDescription(event.target.value)}
            placeholder="Was würde Ihre Arbeit im ETAIN Hub erleichtern?"
            rows={6}
            maxLength={5000}
            autoFocus
          />
        </Field>
      </FormDialog>

      <DialogHandler
        open={mobileOpen}
        onOpenChange={setMobileOpen}
        title={administrationMode ? "Administration" : "ETAIN Hub"}
        description={session.activeTenant.name}
        size="small"
      >
        {session.tenants.length >= 2 ? (
          <div className="mobile-tenant-switcher">
            <label className="ui-sr-only" htmlFor="mobile-tenant-switcher">
              Mandant wechseln
            </label>
            <Select
              id="mobile-tenant-switcher"
              value={session.activeTenant.id}
              onChange={(event) => handleTenantChange(event.target.value)}
              disabled={switchTenant.isPending}
              aria-label="Mandant wechseln"
            >
              {session.tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </div>
        ) : null}
        {administrationMode ? (
          <AdministrationNavigation
            items={administrationNav}
            activePath={administrationSection && adminSectionPath(administrationSection.key)}
            onNavigate={() => setMobileOpen(false)}
          />
        ) : (
          <Navigation items={visibleNav} onNavigate={() => setMobileOpen(false)} />
        )}
        <div className="mobile-documentation-link">
          <HelpLink onNavigate={() => setMobileOpen(false)} />
          <span>Version {appVersion}</span>
        </div>
      </DialogHandler>
    </div>
  );
}
